import getLocalizedText from "../../../Utils";

/**
 * Finds an element by dot-notation path inside nested collections/lists.
 * Returns the element EXACTLY at that path.
 */
function findElementByPath(elements, pathStr) {
  if (!pathStr) return null;

  const parts = pathStr.split(".");
  let current = elements;

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (!Array.isArray(current)) return null;

    const next = current.find((el) => el.idShort === part);
    if (!next) return null;

    const isLast = i === parts.length - 1;
    if (isLast) return next;

    // Prepare to go deeper only if it is a collection/list
    const isCollection =
      next.modelType === "SubmodelElementCollection" ||
      next.modelType === "SubmodelElementList";

    if (!isCollection) return null;

    current = next.value || [];
  }

  return null;
}

export default function renderPublicInformationSubmodelPdf(builder, submodel, locale) {
  const title = getLocalizedText(
    submodel.displayName || submodel.idShort,
    "N/A",
    builder.locale
  );

  const description = getLocalizedText(
    submodel.description,
    "No description available.",
    builder.locale
  );

  builder.sectionHeader(title);
  builder.textBlock(description);
  builder.addDividerLine();

  const elements = submodel.submodelElements || [];

  const allowedPaths = [
    "URIOfTheProduct",
    "ManufacturerProductRoot",
    "ManufacturerProductFamily",
    "ManufacturerProductType",
    "SerialNumber",
    "YearOfConstruction",
    "DateOfManufacture",
    "SoftwareVersion",
    "FirmwareVersion",
    "HardwareVersion",
    "CountryOfOrigin",

    "GeneralInformation",
    "GeneralInformation.ManufacturerName",
    "GeneralInformation.ManufacturerProductDesignation",
    "GeneralInformation.ManufacturerArticleNumber",

    "ProductClassifications",
    "ProductClassifications.ProductClassificationItem1.ProductClassificationSystem",
    "ProductClassifications.ProductClassificationItem1.ClassificationSystemVersion",

    "GeneralInformation",
    "ContactInformation.NationalCode",
    "ContactInformation.Language",
    "ContactInformation.TimeZone",
    "ContactInformation.CityTown",
    "ContactInformation.Company",
    "ContactInformation.Street",
    "ContactInformation.Zipcode",
    "ContactInformation.StateCounty",
    "ContactInformation.AddressOfAdditionalLink",
    "ContactInformation.Phone.TelephoneNumber",
    "ContactInformation.Fax.FaxNumber",
    "ContactInformation.Email.EmailAddress",

    "CircularEconomy",
    "CircularEconomy.WarrantyPeriod",
    "CircularEconomy.RecommendedPeriodOfUse",
    "CircularEconomy.MaximumPeriodOfUse",
    "CircularEconomy.PassingOfRisk",

    "CircularEconomy.ReturnAddress.Company",
    "CircularEconomy.ReturnAddress.Street",
    "CircularEconomy.ReturnAddress.CityTown",
    "CircularEconomy.ReturnAddress.Zipcode",
    "CircularEconomy.ReturnAddress.NationalCode",
    "CircularEconomy.ReturnAddress.StateCounty",

    "CircularEconomy.Repairability.RepairInformation",
    "CircularEconomy.Repairability.DisassemblyInformation",
    "CircularEconomy.Repairability.SparePartsCatalog",

    "CircularEconomy.Recycling.SafetyInstructions",
    "CircularEconomy.Recycling.CollectionPoints",

    "CircularEconomy.Recycling.Material.Material_1.RecycledContentUsed",
    "CircularEconomy.Recycling.Material.Material_1.DegreeOfRecycling",
    "CircularEconomy.Recycling.Material.Material_1.RecyclingNote",

    "CircularEconomy.Remanufacturing.RemanufacturingCycles",

    "Markings.MarkingCE",
    "Markings.MarkingCE.IssueDate",
    "Markings.MarkingCE.ExpiryDate",
    "Markings.MarkingCE.MarkingAdditionalText1",

    "FurtherInformation",
    "FurtherInformation.TextStatement1",
    "FurtherInformation.ValidDate"
  ];

  // Find all allowed items
  const filtered = [];
  for (const path of allowedPaths) {
    const el = findElementByPath(elements, path);
    if (el) {
      filtered.push({ path, el });
    }
  }

  if (filtered.length === 0) {
    builder.textBlock("No public information available.");
    return;
  }

  // Render in order
  filtered.forEach(({ el }) => renderElementPublic(builder, el, 1));
}

/**
 * Public renderer:
 * - If element is a collection/list: only render a header (no recursion).
 * - If element is a property: render key/value.
 */
function renderElementPublic(builder, el, indent) {
  const type = el.modelType;
  const displayName = getLocalizedText(
    el.displayName,
    el.idShort,
    builder.locale
  );

  const isCollection =
    type === "SubmodelElementCollection" ||
    type === "SubmodelElementList";

  if (isCollection) {
    // A collection included in allowedPaths should act as a grouping header
    builder.sectionHeaderSmall(displayName);
    return;
  }

  // Normal property element
  const value = getLocalizedText(el.value, "N/A", builder.locale);
  builder.keyValueRow(displayName, value, indent);
}
